import { useEffect, useState } from 'react';

/**
 * Live Deribit market data from its PUBLIC JSON-RPC WebSocket (no API key,
 * CORS-friendly) — used by the dedicated Deribit trade ticket so prices, the
 * order book and 24h stats reflect Deribit itself, not a Binance look-alike.
 * Order placement still goes through our backend approval flow.
 *
 * Deribit instrument names ("BTC_USDC-PERPETUAL", "BTC_USDC") differ from the
 * platform's "BTCUSDT" dialect, so callers pass the real Deribit instrument name.
 */
const WS_URL = 'wss://www.deribit.com/ws/api/v2';

export interface DeribitTicker {
  price: number;
  markPrice: number;
  changePct: number;
  high: number;
  low: number;
  volumeUsd: number;
  bid: number;
  ask: number;
}

export interface DeribitBookLevel { price: number; qty: number; total: number; }
export interface DeribitBook { bids: DeribitBookLevel[]; asks: DeribitBookLevel[]; }

interface DeribitMarket {
  ticker: DeribitTicker | null;
  book: DeribitBook;
}

/** Live ticker + order book for one Deribit instrument over a single public WS. */
export function useDeribitMarket(instrument: string | undefined, depth = 12): DeribitMarket {
  const [ticker, setTicker] = useState<DeribitTicker | null>(null);
  const [book, setBook] = useState<DeribitBook>({ bids: [], asks: [] });

  useEffect(() => {
    if (!instrument) {
      setTicker(null);
      setBook({ bids: [], asks: [] });
      return;
    }
    setTicker(null);
    setBook({ bids: [], asks: [] });

    const tickerCh = `ticker.${instrument}.100ms`;
    const bookCh = `book.${instrument}.none.20.100ms`;
    let ws: WebSocket | null = new WebSocket(WS_URL);

    const toLevels = (arr: [number, number][] | [string, number, number][]): DeribitBookLevel[] => {
      let run = 0;
      // Snapshot levels are [price, amount]; change deltas are [action, price, amount].
      const norm = arr.map((l) => (l.length === 3 ? [l[1] as number, l[2] as number] : (l as [number, number])));
      return norm
        .filter(([, qty]) => qty > 0)
        .slice(0, depth)
        .map(([price, qty]) => {
          run += qty;
          return { price, qty, total: run };
        });
    };

    ws.onopen = () => {
      ws?.send(JSON.stringify({
        jsonrpc: '2.0', id: 1, method: 'public/subscribe',
        params: { channels: [tickerCh, bookCh] },
      }));
    };
    ws.onmessage = (ev) => {
      let msg: { params?: { channel?: string; data?: Record<string, unknown> } };
      try { msg = JSON.parse(ev.data); } catch { return; }
      const channel = msg.params?.channel;
      const d = msg.params?.data;
      if (!channel || !d) return;
      if (channel === tickerCh) {
        const stats = (d.stats ?? {}) as Record<string, number>;
        setTicker({
          price: Number(d.last_price),
          markPrice: Number(d.mark_price),
          changePct: Number(stats.price_change ?? 0),
          high: Number(stats.high ?? 0),
          low: Number(stats.low ?? 0),
          volumeUsd: Number(stats.volume_usd ?? 0),
          bid: Number(d.best_bid_price),
          ask: Number(d.best_ask_price),
        });
      } else if (channel === bookCh) {
        const bids = (d.bids ?? []) as [number, number][];
        const asks = (d.asks ?? []) as [number, number][];
        setBook({ bids: toLevels(bids), asks: toLevels(asks) });
      }
    };

    return () => {
      const sock = ws;
      ws = null;
      try { sock?.close(); } catch { /* already closed */ }
    };
  }, [instrument, depth]);

  return { ticker, book };
}

/** Compact price formatter that adapts decimals to magnitude (shared with the ticket). */
export function fmtDeribitPrice(n: number | undefined): string {
  if (n == null || Number.isNaN(n) || n === 0) return '—';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(5);
}

// Keep a ref helper available for future polling fallbacks without re-importing.
export const __deribitWsUrl = WS_URL;
export type { DeribitMarket };
